import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Line } from 'react-chartjs-2';
import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  defaults
} from 'chart.js';
import type { ChartData, ChartOptions, Plugin } from 'chart.js';

import { PlayerFace } from './PlayerFace';
import { theme } from '../../tailwind.config';
import type { TransferRecommendation } from '@api-service/recommendations';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

defaults.font.family = 'Open Sans';

// Canvas text cannot resolve CSS variables, and next/font registers Open Sans
// under a generated family name exposed via --font-open-sans; the literal
// 'Open Sans' family silently falls back to a system font on the canvas.
const OPEN_SANS_CSS_VARIABLE = '--font-open-sans';
const FALLBACK_FONT_FAMILY = 'Open Sans';

const useResolvedChartFontFamily = (
  elementRef: React.RefObject<HTMLElement | null>
) => {
  const [fontFamily, setFontFamily] = useState(FALLBACK_FONT_FAMILY);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;
    const resolvedFamily = window
      .getComputedStyle(element)
      .getPropertyValue(OPEN_SANS_CSS_VARIABLE)
      .trim();
    if (resolvedFamily) setFontFamily(resolvedFamily);
  }, [elementRef]);

  return fontFamily;
};

const ACTIVE_LINE_COLOUR = theme.colors.primary[600];
const INACTIVE_LINE_COLOUR = theme.colors.neutral[400];
const GRID_COLOUR = theme.colors.neutral[200];
const AXIS_LABEL_COLOUR = theme.colors.neutral[600];
const BADGE_DIAMETER_PX = 28;
const BADGE_HALO_PADDING_PX = 3;
const ACTIVE_LINE_WIDTH = 3;
const INACTIVE_LINE_WIDTH = 2;
const ACTIVE_POINT_RADIUS = 6;
const INACTIVE_POINT_RADIUS = 4;
const Y_AXIS_GRACE = 1;

type PredictedPointsChartProps = {
  inPlayer: TransferRecommendation['in'];
  outPlayer: TransferRecommendation['out'];
  inImageUrl?: string;
  outImageUrl?: string;
  inKitImageUrl?: string;
  outKitImageUrl?: string;
  /** Rendered on the same row as the legend pills (stacked on mobile). */
  header?: ReactNode;
};

type PlayerRole = 'in' | 'out';

type LineSeries = {
  label: string;
  fixtures: TransferRecommendation['in']['fixtures'];
};

const seriesFor = (player: TransferRecommendation['in']): LineSeries => ({
  label: player.displayName,
  fixtures: player.fixtures
});

const buildLabels = (fixtures: LineSeries[]): string[] => {
  const gameweeks = new Set<number>();
  for (const series of fixtures) {
    for (const fixture of series.fixtures) {
      gameweeks.add(fixture.gameweek);
    }
  }
  return [...gameweeks]
    .sort((a, b) => a - b)
    .map((gameweek) => `GW${gameweek}`);
};

const pointsByGameweek = (series: LineSeries): Map<number, number> => {
  const map = new Map<number, number>();
  for (const fixture of series.fixtures) {
    const existing = map.get(fixture.gameweek) ?? 0;
    map.set(fixture.gameweek, existing + (fixture.predictedPoints ?? 0));
  }
  return map;
};

const opponentByGameweek = (series: LineSeries): Map<number, string> => {
  const map = new Map<number, string>();
  for (const fixture of series.fixtures) {
    if (!map.has(fixture.gameweek)) {
      map.set(fixture.gameweek, fixture.opponent.shortName);
    }
  }
  return map;
};

/** Preloads a badge image and caches it for canvas draws. */
const loadBadgeImage = (
  shortName: string,
  cache: Map<string, HTMLImageElement | null>
): HTMLImageElement | null => {
  if (cache.has(shortName)) {
    return cache.get(shortName) ?? null;
  }
  if (typeof window === 'undefined') return null;
  const image = new Image();
  image.src = `/badges/${shortName}.png`;
  cache.set(shortName, image);
  return image;
};

export const PredictedPointsChart = ({
  inPlayer,
  outPlayer,
  inImageUrl,
  outImageUrl,
  inKitImageUrl,
  outKitImageUrl,
  header
}: PredictedPointsChartProps) => {
  const [activeRole, setActiveRole] = useState<PlayerRole>('in');
  const badgeCacheRef = useRef<Map<string, HTMLImageElement | null>>(new Map());
  const chartRef = useRef<ChartJS<'line'> | null>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const chartFontFamily = useResolvedChartFontFamily(sectionRef);

  const inSeries = useMemo(() => seriesFor(inPlayer), [inPlayer]);
  const outSeries = useMemo(() => seriesFor(outPlayer), [outPlayer]);

  const labels = useMemo(
    () => buildLabels([inSeries, outSeries]),
    [inSeries, outSeries]
  );

  const inPointsMap = useMemo(() => pointsByGameweek(inSeries), [inSeries]);
  const outPointsMap = useMemo(() => pointsByGameweek(outSeries), [outSeries]);
  const inOpponents = useMemo(() => opponentByGameweek(inSeries), [inSeries]);
  const outOpponents = useMemo(
    () => opponentByGameweek(outSeries),
    [outSeries]
  );

  const gameweekNumbers = useMemo(
    () => labels.map((label) => Number.parseInt(label.replace(/^GW/, ''), 10)),
    [labels]
  );

  const inPoints = useMemo(
    () => gameweekNumbers.map((gameweek) => inPointsMap.get(gameweek) ?? null),
    [gameweekNumbers, inPointsMap]
  );
  const outPoints = useMemo(
    () => gameweekNumbers.map((gameweek) => outPointsMap.get(gameweek) ?? null),
    [gameweekNumbers, outPointsMap]
  );

  // Preload all badge images on mount so the canvas plugin can draw them
  // synchronously once the chart paints.
  useEffect(() => {
    const codes = new Set<string>();
    for (const fixture of inPlayer.fixtures) {
      codes.add(fixture.opponent.shortName);
    }
    for (const fixture of outPlayer.fixtures) {
      codes.add(fixture.opponent.shortName);
    }
    let isMounted = true;
    codes.forEach((code) => {
      const image = loadBadgeImage(code, badgeCacheRef.current);
      if (image && !image.complete) {
        image.onload = () => {
          if (isMounted) chartRef.current?.update('none');
        };
      }
    });
    return () => {
      isMounted = false;
    };
  }, [inPlayer.fixtures, outPlayer.fixtures]);

  const activePoints = activeRole === 'in' ? inPoints : outPoints;
  const inactivePoints = activeRole === 'in' ? outPoints : inPoints;
  const activeOpponents = activeRole === 'in' ? inOpponents : outOpponents;
  const activeLabel =
    activeRole === 'in' ? inPlayer.displayName : outPlayer.displayName;
  const inactiveLabel =
    activeRole === 'in' ? outPlayer.displayName : inPlayer.displayName;

  const data: ChartData<'line'> = {
    labels,
    datasets: [
      {
        label: inactiveLabel,
        data: inactivePoints,
        borderColor: INACTIVE_LINE_COLOUR,
        backgroundColor: INACTIVE_LINE_COLOUR,
        borderWidth: INACTIVE_LINE_WIDTH,
        tension: 0.45,
        pointRadius: INACTIVE_POINT_RADIUS,
        pointBackgroundColor: theme.colors.white,
        pointBorderColor: INACTIVE_LINE_COLOUR,
        pointBorderWidth: 2,
        pointHitRadius: 16,
        fill: false,
        spanGaps: true
      },
      {
        label: activeLabel,
        data: activePoints,
        borderColor: ACTIVE_LINE_COLOUR,
        backgroundColor: (context) => {
          const ctx = context.chart.ctx;
          const area = context.chart.chartArea;
          if (!area) return 'rgba(0, 157, 229, 0.08)';
          const gradient = ctx.createLinearGradient(
            0,
            area.top,
            0,
            area.bottom
          );
          gradient.addColorStop(0, 'rgba(0, 157, 229, 0.22)');
          gradient.addColorStop(1, 'rgba(0, 157, 229, 0)');
          return gradient;
        },
        borderWidth: ACTIVE_LINE_WIDTH,
        tension: 0.45,
        pointRadius: ACTIVE_POINT_RADIUS,
        pointBackgroundColor: theme.colors.white,
        pointBorderColor: ACTIVE_LINE_COLOUR,
        pointBorderWidth: 2,
        pointHitRadius: 16,
        fill: 'origin',
        spanGaps: true
      }
    ]
  };

  // The chart is constructed once by react-chartjs-2 and only its data and
  // options are updated on re-render — plugins are baked in at creation. A
  // plugin that closed over this render's gameweeks/opponents would keep
  // drawing the first transfer's crests forever, so the draw callback reads
  // the latest values through a ref instead.
  const badgeDrawDataRef = useRef({ gameweekNumbers, activeOpponents });
  badgeDrawDataRef.current = { gameweekNumbers, activeOpponents };

  // Custom plugin: draws the active line's opponent crests as floating
  // circular badges anchored on each data point. The white halo lifts the
  // crest off the chart and creates the "sticker" effect from the design.
  const activeBadgePlugin: Plugin<'line'> = {
    id: 'activeBadgePlugin',
    afterDatasetsDraw: (chart) => {
      const meta = chart.getDatasetMeta(1);
      if (!meta || meta.hidden) return;
      const ctx = chart.ctx;
      meta.data.forEach((point, index) => {
        const gameweek = badgeDrawDataRef.current.gameweekNumbers[index];
        if (typeof gameweek !== 'number') return;
        const opponentCode =
          badgeDrawDataRef.current.activeOpponents.get(gameweek);
        if (!opponentCode) return;
        const image = loadBadgeImage(opponentCode, badgeCacheRef.current);
        if (!image || !image.complete || image.naturalWidth === 0) return;

        const { x, y } = point.getProps(['x', 'y'], true);
        const haloRadius = BADGE_DIAMETER_PX / 2 + BADGE_HALO_PADDING_PX;

        ctx.save();
        ctx.beginPath();
        ctx.arc(x, y, haloRadius, 0, Math.PI * 2);
        ctx.fillStyle = theme.colors.white;
        ctx.shadowColor = 'rgba(15, 23, 42, 0.18)';
        ctx.shadowBlur = 12;
        ctx.shadowOffsetY = 3;
        ctx.fill();
        ctx.restore();

        ctx.save();
        ctx.beginPath();
        ctx.arc(x, y, BADGE_DIAMETER_PX / 2, 0, Math.PI * 2);
        ctx.closePath();
        ctx.clip();
        ctx.drawImage(
          image,
          x - BADGE_DIAMETER_PX / 2,
          y - BADGE_DIAMETER_PX / 2,
          BADGE_DIAMETER_PX,
          BADGE_DIAMETER_PX
        );
        ctx.restore();
      });
    }
  };

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    },
    scales: {
      x: {
        offset: true,
        grid: { display: false },
        border: { display: false },
        ticks: {
          font: { size: 12, family: chartFontFamily, weight: 'bold' },
          color: AXIS_LABEL_COLOUR,
          padding: 10
        }
      },
      y: {
        beginAtZero: true,
        grace: Y_AXIS_GRACE,
        border: { display: false },
        grid: {
          color: GRID_COLOUR
        },
        ticks: {
          stepSize: 2,
          font: { size: 11, family: chartFontFamily },
          color: AXIS_LABEL_COLOUR,
          padding: 8
        }
      }
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        backgroundColor: theme.colors.neutral[800],
        titleFont: { family: chartFontFamily, size: 12, weight: 'bold' },
        bodyFont: { family: chartFontFamily, size: 12 },
        padding: 10,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          title: (items) => items[0]?.label ?? '',
          label: (item) =>
            `${item.dataset.label}: ${(item.parsed.y ?? 0).toFixed(1)} pts`
        }
      }
    },
    layout: {
      padding: {
        top: BADGE_DIAMETER_PX,
        right: 4,
        left: 4,
        bottom: 4
      }
    }
  };

  return (
    <section
      ref={sectionRef}
      className="flex flex-col gap-3.5"
      data-cy="predicted-points-chart"
    >
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between md:gap-4">
        {header}
        <div
          className="flex flex-shrink-0 items-center gap-3"
          data-cy="predicted-points-legend"
        >
          <LegendButton
            player={inPlayer}
            imageUrl={inImageUrl}
            kitImageUrl={inKitImageUrl}
            colour={
              activeRole === 'in' ? ACTIVE_LINE_COLOUR : INACTIVE_LINE_COLOUR
            }
            isActive={activeRole === 'in'}
            onClick={() => setActiveRole('in')}
          />
          <LegendButton
            player={outPlayer}
            imageUrl={outImageUrl}
            kitImageUrl={outKitImageUrl}
            colour={
              activeRole === 'out' ? ACTIVE_LINE_COLOUR : INACTIVE_LINE_COLOUR
            }
            isActive={activeRole === 'out'}
            onClick={() => setActiveRole('out')}
          />
        </div>
      </div>

      <div className="relative h-[190px] w-full md:h-[270px]">
        <Line
          ref={(chart) => {
            chartRef.current = chart ?? null;
          }}
          data={data}
          options={options}
          plugins={[activeBadgePlugin]}
        />
      </div>
    </section>
  );
};

type LegendButtonProps = {
  player: TransferRecommendation['in'];
  imageUrl?: string;
  kitImageUrl?: string;
  colour: string;
  isActive: boolean;
  onClick: () => void;
};

const LegendButton = ({
  player,
  imageUrl,
  kitImageUrl,
  colour,
  isActive,
  onClick
}: LegendButtonProps) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex items-center gap-2 rounded-full border pl-1 pr-3 py-1 transition-colors ${
      isActive
        ? 'border-primary-200 bg-primary-50'
        : 'border-neutral-200 bg-white hover:bg-neutral-50'
    }`}
    data-cy="predicted-points-legend-button"
    data-cy-active={isActive}
  >
    <span
      className="relative inline-flex items-center justify-center"
      style={{
        boxShadow: isActive ? `0 0 0 2px ${colour}` : 'none',
        borderRadius: '9999px'
      }}
    >
      <PlayerFace
        imageUrl={imageUrl}
        kitImageUrl={kitImageUrl}
        teamShortName={player.team.shortName}
        position={player.position}
        displayName={player.displayName}
        className="h-7 w-7 rounded-full bg-neutral-100"
      />
    </span>
    <span
      className={`text-sm ${
        isActive ? 'font-semibold text-neutral-800' : 'text-neutral-500'
      }`}
    >
      {player.displayName}
    </span>
  </button>
);
